import { useEffect, useRef, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";

import { fetchCurrentUser } from "@/api/auth";
import { useAuthStore } from "@/stores/auth";

// Check interval: 5 minutes (in ms)
const CHECK_INTERVAL = 5 * 60 * 1000;

// Throttle duration: 1 minute (in ms)
const THROTTLE_DURATION = 60 * 1000;

/**
 * Proactively validates the session to detect expiration before user actions fail.
 *
 * This hook implements two strategies:
 * 1. Check session validity when window regains focus (user returns to tab)
 * 2. Periodic background checks every 5 minutes
 *
 * If the session is invalid, the global 401 interceptor will handle logout/redirect.
 */
export function useSessionValidation() {
  const user = useAuthStore((state) => state.user);
  const lastCheckRef = useRef<number>(0);

  const { refetch } = useQuery({
    queryKey: ["session-validation"],
    queryFn: fetchCurrentUser,
    enabled: false,
    retry: false, // Don't retry - let the 401 interceptor handle auth failures
    staleTime: Infinity, // Don't auto-refetch, we control when to check
  });

  const performCheck = useCallback(() => {
    const now = Date.now();
    // Throttle checks to avoid excessive requests
    if (now - lastCheckRef.current < THROTTLE_DURATION) {
      return;
    }
    lastCheckRef.current = now;
    void refetch();
  }, [refetch]);

  // Strategy 1: Check on window focus (user returns to tab)
  // Using visibilitychange is more reliable than focus for tab switching
  useEffect(() => {
    if (!user) {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        performCheck();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [user, performCheck]);

  // Strategy 2: Periodic background checks every 5 minutes
  useEffect(() => {
    if (!user) {
      return;
    }

    const interval = setInterval(() => {
      performCheck();
    }, CHECK_INTERVAL);

    return () => {
      clearInterval(interval);
    };
  }, [user, performCheck]);
}
